import { useNavigate } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import usePageSeo from "../../hooks/usePageSeo";
import logoIcon from "../../assets/logo-icon.png";
import "../LegalPages.css";

/**
 * /pay/terms — "תנאי תשלום": the terms a patient accepts on /pay/:code before
 * moving to the Grow hosted payment page. Content follows Grow's merchant
 * site-check list (2026-11-03): delivery policy, age limit, privacy, liability,
 * cancellation per חוק הגנת הצרכן תשמ"א-1981, business contact details.
 * Indexed (prerendered). Error code range: CK-6305 (none used — static page).
 */

// Company details shown in §7. Keep in sync with Footer.js / TermsOfServicePage.js.
const COMPANY = {
  brand: "קליניקיט (CliniKit)",
  legalName: "ריבוע ורוד",
  vatNumber: "207945882",
  address: "נחל לכיש 2, שדרות",
  email: "office@clinikit.co.il",
  whatsapp: "054-3073737",
};

const LAST_UPDATED = "3 בנובמבר 2026";

function GrowPaymentTermsPage() {
  const navigate = useNavigate();

  usePageSeo({
    title: "תנאי תשלום",
    description:
      "תנאי התשלום לתשלומים המבוצעים למטפלים ולקליניקות דרך מערכת CliniKit: אספקת השירות, פרטיות, אחריות וביטול עסקה לפי חוק הגנת הצרכן.",
    path: "/pay/terms",
  });

  return (
    <div className="legal-page">
      <div className="legal-header">
        <button className="legal-back-btn" onClick={() => navigate(-1)}>
          <ArrowRight size={20} weight="bold" />
          <span>חזרה</span>
        </button>
        <div className="legal-logo-group">
          <span className="legal-logo-text">CliniKit</span>
          <img src={logoIcon} alt="CliniKit" className="legal-logo-icon" />
        </div>
      </div>

      <div className="legal-content">
        <h1>תנאי תשלום</h1>
        <div className="legal-text">
          <p>תוכן עניינים</p>
          <p>1. כללי</p>
          <p>2. מדיניות אספקת השירות</p>
          <p>3. הגבלת גיל</p>
          <p>4. פרטיות ואבטחת מידע</p>
          <p>5. אחריות</p>
          <p>6. ביטול עסקה והחזרים</p>
          <p>7. פרטי קשר</p>
          <p>8. עדכון התנאים</p>

          <p>1. כללי</p>
          <p>1.1. "קליניקיט" (CliniKit, להלן: "הפלטפורמה"), המופעלת על ידי {COMPANY.legalName}, עוסק מורשה {COMPANY.vatNumber}, היא מערכת תוכנה לניהול קליניקות המשמשת מטפלים וקליניקות פרטיות (להלן: "הקליניקה"). הפלטפורמה מאפשרת לקליניקה לשלוח למטופל בקשת תשלום דיגיטלית עבור שירותי טיפול שניתנו או שייקבעו.</p>
          <p>1.2. בית העסק בכל עסקת תשלום הוא הקליניקה ששמה, טלפון וכתובתה מופיעים בעמוד התשלום. הפלטפורמה אינה צד לעסקה בין המטופל לקליניקה ואינה נותנת את שירות הטיפול.</p>
          <p>1.3. סליקת התשלום מתבצעת באמצעות חברת Grow (משולם בע"מ), ספקית שירותי סליקה מורשית בישראל, בעמוד תשלום מאובטח. פרטי כרטיס האשראי מוזנים ישירות בעמוד של Grow ואינם נשמרים בפלטפורמה.</p>
          <p>1.4. סימון תיבת האישור בעמוד התשלום ולחיצה על "לתשלום מאובטח" מהווים הסכמה לתנאים אלה, לתנאי השימוש ולמדיניות הפרטיות של הפלטפורמה.</p>

          <p>2. מדיניות אספקת השירות</p>
          <p>2.1. השירות הנרכש הוא שירות טיפולי (פגישת טיפול, סדרת טיפולים, כרטיסייה או שירות מקצועי אחר) המסופק על ידי הקליניקה. השירות ניתן בקליניקה, בביקור בית או מרחוק (וידאו/טלפון), במועד שנקבע בין המטופל לקליניקה.</p>
          <p>2.2. לא מתבצע משלוח פיזי של מוצרים. פירוט השירות והסכום לתשלום מוצגים בעמוד התשלום לפני אישור העסקה.</p>
          <p>2.3. לאחר השלמת התשלום נשלח למטופל אישור תשלום, ובמידה שהקליניקה הפעילה הפקת מסמכים אוטומטית, גם קבלה או חשבונית דיגיטלית, באמצעות וואטסאפ, SMS או דוא"ל. לבקשת מסמך נוסף יש לפנות לקליניקה.</p>
          <p>2.4. במקרה של תקלה בביצוע התשלום, אם חויבתם ולא התקבל אישור, יש לפנות לקליניקה בטלפון המופיע בעמוד התשלום. הקליניקה תברר את סטטוס העסקה מול חברת הסליקה.</p>

          <p>3. הגבלת גיל</p>
          <p>3.1. ביצוע תשלום בכרטיס אשראי באמצעות עמוד התשלום מותר למי שמלאו לו 18 שנים ומעלה ובעל כרטיס אשראי בתוקף על שמו, או מי שהוסמך כדין על ידי בעל הכרטיס.</p>
          <p>3.2. תשלום עבור טיפול לקטין יבוצע על ידי הורה או אפוטרופוס.</p>

          <p>4. פרטיות ואבטחת מידע</p>
          <p>4.1. לצורך ביצוע התשלום נאספים בעמוד התשלום שם מלא, מספר טלפון וכתובת דוא"ל (אופציונלי). פרטים אלה משמשים לזיהוי התשלום, לשליחת אישור התשלום והקבלה ולהתאמת התשלום לרשומת הטיפול בקליניקה.</p>
          <p>4.2. פרטי אמצעי התשלום (מספר כרטיס, תוקף, CVV) נמסרים ישירות לחברת Grow ומעובדים על ידה בהתאם לתקן PCI DSS ולמדיניות הפרטיות שלה. הפלטפורמה מקבלת מחברת הסליקה אסמכתא, סכום, אמצעי תשלום וארבע ספרות אחרונות של הכרטיס בלבד.</p>
          <p>4.3. המידע נשמר במאגרי הפלטפורמה בהתאם למדיניות הפרטיות שלה, הזמינה בכתובת clinikit.co.il/privacy-policy, ובהתאם לחוק הגנת הפרטיות, התשמ"א-1981.</p>
          <p>4.4. המידע אינו נמכר ואינו מועבר לצדדים שלישיים, למעט לקליניקה שעבורה בוצע התשלום, לחברת הסליקה לצורך ביצוע העסקה, ולגורמים הנדרשים על פי דין.</p>

          <p>5. אחריות</p>
          <p>5.1. הקליניקה היא האחראית הבלעדית לשירות הטיפולי, לטיבו, למועדו ולתוצאותיו, וכן לכל מצג או התחייבות שניתנו למטופל.</p>
          <p>5.2. הפלטפורמה אחראית לתפעול התקין של מערכת שליחת בקשות התשלום בלבד. הפלטפורמה, מנהליה, עובדיה ומי מטעמה לא יהיו אחראים לכל נזק ישיר או עקיף שייגרם כתוצאה מהשירות הטיפולי, מהשימוש בו או מאי-אספקתו, או משיבוש בשירותי חברת הסליקה.</p>
          <p>5.3. אין באמור כדי לגרוע מזכויות המטופל על פי חוק הגנת הצרכן או כל דין אחר שאינו ניתן להתניה.</p>

          <p>6. ביטול עסקה והחזרים</p>
          <p>6.1. ביטול עסקה ייעשה בהתאם להוראות חוק הגנת הצרכן, התשמ"א-1981 ותקנות הגנת הצרכן (ביטול עסקה), התשע"א-2010.</p>
          <p>6.2. ניתן לבטל עסקה לרכישת שירות בתוך 14 ימים מיום ביצוע העסקה או מיום קבלת מסמך פרטי העסקה, לפי המאוחר, ובלבד שהביטול ייעשה לפחות שני ימים שאינם ימי מנוחה לפני המועד שבו אמור השירות להינתן.</p>
          <p>6.3. בביטול שלא עקב פגם או אי-התאמה, רשאית הקליניקה לגבות דמי ביטול בשיעור של 5% ממחיר העסקה או 100 ש"ח, לפי הנמוך מביניהם, בהתאם לחוק.</p>
          <p>6.4. הודעת ביטול תימסר לקליניקה בטלפון המופיע בעמוד התשלום או בכל דרך אחרת שהקליניקה מאפשרת. ההחזר הכספי יבוצע באמצעי התשלום המקורי בתוך 14 ימים מקבלת הודעת הביטול.</p>
          <p>6.5. לקליניקה עשויה להיות מדיניות ביטול והיעדרות נוספת (לדוגמה, לפגישות שבוטלו בהתראה קצרה) המוצגת למטופל על ידי הקליניקה. מדיניות זו אינה גורעת מזכויות המטופל על פי חוק.</p>
          <p>6.6. תשלום עבור שירות שכבר ניתן במלואו אינו ניתן לביטול, למעט במקרים הקבועים בחוק.</p>

          <p>7. פרטי קשר</p>
          <p>7.1. בשאלות הנוגעות לשירות הטיפולי, לביטול עסקה או להחזר יש לפנות ישירות לקליניקה, בטלפון ובכתובת המופיעים בעמוד התשלום.</p>
          <p>7.2. בשאלות הנוגעות לתפעול עמוד התשלום או לפלטפורמה ניתן לפנות ל{COMPANY.brand}:</p>
          <p>• שם העסק: {COMPANY.legalName}, עוסק מורשה {COMPANY.vatNumber}</p>
          <p>• דוא"ל: {COMPANY.email}</p>
          <p>• וואטסאפ: {COMPANY.whatsapp}</p>
          <p>• כתובת: {COMPANY.address}</p>
          <p>• שעות פעילות: א'-ה' 09:00-18:00, ו' וערבי חג 09:00-13:00</p>

          <p>8. עדכון התנאים</p>
          <p>8.1. הפלטפורמה רשאית לעדכן תנאים אלה מעת לעת. הנוסח המחייב הוא הנוסח המפורסם בעמוד זה במועד ביצוע התשלום.</p>
          <p>8.2. על תנאים אלה יחולו דיני מדינת ישראל, וסמכות השיפוט הבלעדית נתונה לבתי המשפט המוסמכים במחוז תל אביב-יפו.</p>
          <p>8.3. עודכן לאחרונה: {LAST_UPDATED}.</p>
        </div>
      </div>
    </div>
  );
}

export default GrowPaymentTermsPage;
